import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DashboardMetric {
  readonly dashboardMetricId: string;
  readonly metricCategory: string;
  readonly metricName: string;
  readonly metricValue: string;
  readonly metricUnit: string | null;
}

/**
 * M25 — AI Platform Dashboard (§16).
 * Surfaces the consolidated AI health metrics and lets an operator recompute
 * the seven-display snapshot on demand. Read-only intelligence: every figure is
 * advisory and traceable back to its source service.
 */
export function M25AiDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetric[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: DashboardMetric[]; total: number }>('/v1/ai/dashboard', { token })
      .then((r) => { if (alive) { setMetrics(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reloadKey]);

  async function recomputeSnapshot(): Promise<void> {
    setRefreshing(true);
    try {
      await apiRequest('/v1/ai/dashboard/snapshot', { method: 'POST', body: {}, token });
      setReloadKey((k) => k + 1);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <main aria-label="ai-dashboard">
      <h1>{t('m25.aiDashboard.title')}</h1>
      <button type="button" onClick={recomputeSnapshot} disabled={refreshing}>
        {refreshing ? t('m25.aiDashboard.recomputing') : t('m25.aiDashboard.recompute')}
      </button>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m25.aiDashboard.loading')}</p> : (
        <>
          <p aria-label="metric-count">{t('m25.aiDashboard.count', { count: total })}</p>
          <table aria-label="ai-metric-table">
            <thead>
              <tr><th>{t('m25.aiDashboard.colCategory')}</th><th>{t('m25.aiDashboard.colMetric')}</th><th>{t('m25.aiDashboard.colValue')}</th><th>{t('m25.aiDashboard.colUnit')}</th></tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.dashboardMetricId}>
                  <td>{m.metricCategory}</td>
                  <td>{m.metricName}</td>
                  <td>{m.metricValue}</td>
                  <td>{m.metricUnit ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
